import React from 'react';
import { TWButton } from '../../../../components/ui';

const ConnectTerminals = ({
  terminals = [],
  hasAccount = false,
  busyTerminalId = '',
  errors = {},
  // '' when the last terminals read succeeded (or none has finished yet).
  // Rendered as its own line whenever it is set — independent of whether
  // `terminals` is also empty — because the common case is a stale-but-real
  // list still on screen: without the error surfaced, a row still offers an
  // enabled Prepare for a terminal that meanwhile got a real Stripe Location,
  // and clicking it fires a second `prepareTerminal`.
  error = '',
  // False until the first read has settled (success OR failure). Before
  // that, `terminals` is still its initial `[]` and `error` is still '' — the
  // exact same shape a confirmed-zero-terminals company has — so without this
  // flag an in-flight first read renders the same "this company has no
  // terminals" sentence a settled empty read would, before the read has
  // actually happened.
  loaded = true,
  onPrepare,
  onPrepareAll,
}) => {
  const remaining = terminals.filter((t) => !t.prepared);
  const showList = terminals.length > 0;

  return (
    <section className="py-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Terminals
        </h3>
        {hasAccount && terminals.length > 0 && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {terminals.length - remaining.length} of {terminals.length} prepared
          </p>
        )}
      </div>

      {!hasAccount ? (
        // Without a Connect account every Prepare fails identically. One
        // sentence beats five copies of the same error.
        <p className="mt-3 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
          This company needs a Stripe account before terminals can be prepared.
        </p>
      ) : (
        <>
          {error && (
            <p className="mt-3 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
              {error}
            </p>
          )}

          {error && !showList ? null : !loaded && !showList ? (
            // The first read has not settled yet — say so rather than the
            // confirmed-empty sentence below, the exact defect class this
            // whole section exists to eliminate.
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Checking for terminals…
            </p>
          ) : terminals.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              This company has no terminals, so none have to be prepared.
            </p>
          ) : (
            <>
              {remaining.length > 0 && (
                <div className="mt-3">
                  <TWButton
                    variant="default"
                    size="sm"
                    label="Prepare all remaining"
                    disabled={!!busyTerminalId}
                    onClick={() => onPrepareAll?.()}
                  />
                </div>
              )}

              <ul className="mt-4 divide-y divide-slate-200 dark:divide-slate-700">
                {terminals.map((terminal) => (
                  <li key={terminal.terminalId} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
                    <span className="w-56 shrink-0 text-sm text-slate-900 dark:text-slate-100">
                      {terminal.name}
                    </span>
                    <span className="w-40 shrink-0 text-xs text-slate-500 dark:text-slate-400">
                      {terminal.storeName}
                      {terminal.code ? ` · ${terminal.code}` : ''}
                    </span>
                    {terminal.prepared ? (
                      <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
                        <span aria-label="Prepared" title="Prepared">✓</span>{' '}
                        {terminal.stripeLocationId}
                      </span>
                    ) : (
                      <TWButton
                        variant="default"
                        size="sm"
                        label="Prepare"
                        loading={busyTerminalId === terminal.terminalId}
                        disabled={!!busyTerminalId}
                        onClick={() => onPrepare?.(terminal.terminalId)}
                      />
                    )}
                    {errors[terminal.terminalId] && (
                      <span className="w-full text-xs text-slate-600 dark:text-slate-300">
                        Couldn’t prepare —{' '}
                        <span>{errors[terminal.terminalId]}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ConnectTerminals;
